/** Iter 639 — vendor panel split · Step 1: register / claim invite
 * (moved verbatim from pages/PartnerProgramPage.jsx). */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ArrowRight, Mail, Lock, User as UserIcon } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { SocialCategoryFields, emptySocial, socialPayload } from "@/components/partner/SocialCategoryCard";
import { ENGAGEMENT_HEADERS, ENGAGEMENT_PARAM, errMsg, Field } from "@/features/vendorPanel/helpers";

export default function RegisterCard({ v }) {
  const { token, invite, inviteExpired, user: currentUser, register, params, setStep } = v;
  const nextStep = () => setStep(2);
  const next = params.get("next") || "/partner-program";
  const prefill = useMemo(() => ({
    email: invite?.email || currentUser?.email || "",
    full_name: invite?.contact_name || currentUser?.full_name || "",
    business_name: invite?.business_name || "",
    business_phone: invite?.phone || "",
    business_address: invite?.address || "",
    business_nzbn: invite?.nzbn || "",
  }), [invite, currentUser]);

  const [f, setF] = useState({
    ...prefill,
    password: "",
  });
  // Iter-338 — optional socials + category collected ON the application.
  const [social, setSocial] = useState(emptySocial());
  const [loading, setLoading] = useState(false);

  // Keep prefill fresh when invite finally resolves
  useEffect(() => {
    setF((prev) => ({ ...prev, ...prefill, password: prev.password }));
  }, [prefill]);

  // Logged-in path → straight to claim
  const claimMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        `/classifieds/engagement/invites/${token}/claim`,
        {},
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Invite claimed — let's verify your business.");
      nextStep();
    },
    onError: (err) => toast.error(errMsg(err, "Couldn't claim invite")),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (inviteExpired) {
      toast.error("This invite has already been used.");
      return;
    }
    if (currentUser) {
      if (token) {
        claimMut.mutate();
        return;
      }
      // Iter-288e P0: signed-in user with no invite token still needs a
      // vendor record before KYC upload can succeed. Attempt vendor
      // creation from the user's stored business info; tolerate 409
      // (already registered — normal for a returning partner) and any
      // other status blocks the wizard so the founder doesn't hit
      // "Register as a vendor first" downstream.
      setLoading(true);
      try {
        const body = {
          business_name: currentUser.business_name || currentUser.full_name || "",
          contact_name: currentUser.full_name || "",
          email: currentUser.email || "",
          contact_email: currentUser.email || "",
          phone: currentUser.business_phone || currentUser.phone || "",
          contact_phone: currentUser.business_phone || currentUser.phone || "",
          physical_address: currentUser.business_address || "",
          business_address: currentUser.business_address || "",
          country: "NZ",
          country_code: "NZ",
          fee_mode: "customer_pays",
          agree_terms: true,
          tos_accepted: true,
        };
        if (currentUser.business_nzbn) {
          body.nzbn = currentUser.business_nzbn;
          body.business_number = currentUser.business_nzbn;
        }
        try {
          await sharedApi.post(
            "/classifieds/engagement/vendors/register",
            body,
            { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
          );
        } catch (vendorErr) {
          if (vendorErr?.response?.status !== 409) throw vendorErr;
        }
        nextStep();
      } catch (err) {
        toast.error(errMsg(err, "Couldn't set up your vendor account"));
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!f.business_name.trim()) {
      toast.error("Business name is required.");
      return;
    }
    // Iter-292 — business fields mandatory to match RegisterPage / backend.
    if ((f.business_nzbn || "").replace(/\D/g, "").length !== 13) {
      toast.error("NZBN is required — must be 13 digits.");
      return;
    }
    if (!(f.business_phone || "").trim()) {
      toast.error("Business phone is required.");
      return;
    }
    if (!(f.business_address || "").trim()) {
      toast.error("Business address is required.");
      return;
    }
    setLoading(true);
    try {
      // Register the account first
      const payload = {
        email: f.email.trim(),
        password: f.password,
        full_name: f.full_name.trim(),
        account_type: "business",
        business_name: f.business_name.trim(),
        rewards_optin: false,
      };
      if (f.business_nzbn) payload.business_nzbn = f.business_nzbn.replace(/\D/g, "");
      if (f.business_phone) payload.business_phone = f.business_phone.trim();
      if (f.business_address) payload.business_address = f.business_address.trim();

      // Prefer the invite-claim path when we have a token; the backend
      // will create the account + claim the invite in a single call.
      if (token) {
        await sharedApi.post(
          `/classifieds/engagement/invites/${token}/claim`,
          payload,
          { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
        );
      } else {
        // No invite token — two-step: (1) create the Allsale auth
        // account, (2) explicitly create the vendor record. Step 2 is
        // required because the backend does NOT chain vendor creation
        // to /auth/register on this platform, so without it the wizard
        // used to advance to KYC while the vendor record didn't exist
        // yet (Iter-288e founder-reported P0). If step 2 fails the
        // whole submit throws → catch → wizard stays on Register.
        await register(payload);
        const vendorBody = {
          business_name: payload.business_name,
          contact_name: payload.full_name,
          email: payload.email,
          contact_email: payload.email,
          phone: payload.business_phone || "",
          contact_phone: payload.business_phone || "",
          physical_address: payload.business_address || "",
          business_address: payload.business_address || "",
          country: "NZ",
          country_code: "NZ",
          fee_mode: "customer_pays",
          agree_terms: true,
          tos_accepted: true,
        };
        if (payload.business_nzbn) {
          vendorBody.nzbn = payload.business_nzbn;
          vendorBody.business_number = payload.business_nzbn;
        }
        // Iter-338 — optional social_handles + store_category.
        // NOTE (verified live): /vendors/register stores handles RAW and
        // drops store_category — only PUT /vendors/me normalises. So we
        // register first, then PUT the socials.
        const socialBody = socialPayload(social);
        try {
          await sharedApi.post(
            "/classifieds/engagement/vendors/register",
            vendorBody,
            { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
          );
        } catch (vendorErr) {
          // 409 = vendor already exists (rare — same email retrying);
          // treat as success so a legit retry can advance to KYC. Any
          // other status blocks the wizard so the founder no longer
          // sees "Register as a vendor first" on KYC upload.
          if (vendorErr?.response?.status !== 409) throw vendorErr;
        }
        if (Object.keys(socialBody).length > 0) {
          try {
            await sharedApi.put(
              "/classifieds/engagement/vendors/me",
              socialBody,
              { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
            );
          } catch {
            // Optional fields must never block the application — the
            // dashboard Social pages card can fix them later.
            toast.message("Social pages couldn't be saved — you can add them from your dashboard.");
          }
        }
      }
      toast.success("Account ready — let's verify your business.");
      nextStep();
    } catch (err) {
      toast.error(errMsg(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const busy = loading || claimMut.isPending;
  const disableSubmit = inviteExpired || busy;

  return (
    <form
      onSubmit={submit}
      noValidate
      className="rounded-2xl bg-white shadow-card p-6 md:p-8 space-y-4"
      data-testid="partner-register-form"
    >
      <h2 className="font-display text-2xl font-black text-slate-900">
        {currentUser ? "Join the Partner Program" : "Create your partner account"}
      </h2>
      <p className="text-sm text-slate-500">
        {currentUser
          ? "Check the account below, then continue to verification."
          : "One login for the Allsale marketplace, classifieds and payouts."}
      </p>
      {/* Iter-418 — account-scoped points: make the acting account
          unmissable BEFORE registration (each login keeps its own
          store + points; no merging). */}
      {currentUser && (
        <div
          data-testid="partner-acting-as"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-slate-700"
        >
          <span className="font-bold text-slate-900">👤 Acting as: {currentUser.email}</span>
          <span className="block mt-0.5 text-xs text-slate-600">
            The store you register — and its rewards points — belong to this login. Not the right
            account? Sign out and sign back in with the one you want first.
          </span>
        </div>
      )}

      {!currentUser && (
        <>
          <Field label="Full name *">
            <div className="relative">
              <UserIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="partner-full-name"
                value={f.full_name}
                onChange={(e) => setF({ ...f, full_name: e.target.value })}
                required
                className="pp-input with-icon"
                placeholder="Jane Doe"
              />
            </div>
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Email *">
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="partner-email"
                  type="email"
                  value={f.email}
                  onChange={(e) => setF({ ...f, email: e.target.value })}
                  required
                  readOnly={Boolean(invite?.email)}
                  className="pp-input with-icon"
                  placeholder="you@yourbusiness.co.nz"
                />
              </div>
            </Field>
            <Field label="Password *">
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="partner-password"
                  type="password"
                  value={f.password}
                  onChange={(e) => setF({ ...f, password: e.target.value })}
                  required
                  minLength={6}
                  className="pp-input with-icon"
                  placeholder="At least 6 characters"
                />
              </div>
            </Field>
          </div>

          <Field label="Business name *">
            <input
              data-testid="partner-business-name"
              value={f.business_name}
              onChange={(e) => setF({ ...f, business_name: e.target.value })}
              required
              className="pp-input"
              placeholder="Hamilton Motors Ltd"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="NZBN *">
              <input
                data-testid="partner-business-nzbn"
                value={f.business_nzbn}
                onChange={(e) => setF({ ...f, business_nzbn: e.target.value.replace(/\D/g, "").slice(0, 13) })}
                inputMode="numeric"
                maxLength={13}
                required
                className="pp-input"
                placeholder="9429000000000"
              />
            </Field>
            <Field label="Phone *">
              <input
                data-testid="partner-business-phone"
                value={f.business_phone}
                onChange={(e) => setF({ ...f, business_phone: e.target.value })}
                inputMode="tel"
                required
                className="pp-input"
                placeholder="021 555 0000"
              />
            </Field>
          </div>

          <Field label="Business address *">
            <input
              data-testid="partner-business-address"
              value={f.business_address}
              onChange={(e) => setF({ ...f, business_address: e.target.value })}
              required
              className="pp-input"
              placeholder="123 Queen Street, Auckland"
            />
          </Field>

          {/* Iter-338 — optional social pages + category (never blocks). */}
          <SocialCategoryFields value={social} onChange={setSocial} />
        </>
      )}

      <button
        type="submit"
        disabled={disableSubmit}
        data-testid="partner-register-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {currentUser ? "Continue to verification" : "Create account & continue"}
      </button>

      {!currentUser && (
        <p className="text-xs text-slate-400 text-center">
          Already have an account?{" "}
          <Link
            to={`/login?next=${encodeURIComponent(next)}`}
            className="font-semibold text-[color:var(--brand-blue)] hover:underline"
            data-testid="partner-login-link"
          >
            Log in
          </Link>
        </p>
      )}
    </form>
  );
}
